import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Descriptions,
  Image,
  Modal,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Timeline,
  Tooltip,
  Typography,
} from 'antd';
import { FilePdfOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { Circle, MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import SITELINKS from 'constants/sitelinks';
import {
  GEOFENCE_RADIUS_METERS,
  useCollectionGeofenceEvents,
} from 'services/collectionGeofenceEvents';
import {
  COLLECTION_EVENT_LABELS,
  COLLECTION_STATUS_COLORS,
  COLLECTION_STATUS_LABELS,
  CollectionLog,
  collectionActor,
  collectionsResultStatus,
  useCollection,
} from 'services/CollectionsV2Service';
import { exportCollectionRemitoV2 } from './collectionRemitoV2';
import defaultMarkerIcon from './mapMarkerIcon';

const { Text } = Typography;

const MapInvalidator: React.FC = () => {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize({ animate: false }), 350);
    return () => clearTimeout(t);
  }, [map]);
  return null;
};

const EVENT_COLORS: Partial<Record<CollectionLog['event'], string>> = {
  collected: 'green',
  received: 'green',
  left: 'orange',
  cancelled: 'red',
  route_closed: 'volcano',
};

const fmt = (value: string) => dayjs(value).format('DD/MM/YYYY HH:mm');

const eventLabel = (log: CollectionLog): string =>
  log.event === 'route_closed' && log.toStatus !== 'not_collected'
    ? 'La ruta cerró con la colecta en el camión: la tiene el driver'
    : (COLLECTION_EVENT_LABELS[log.event] ?? log.event);

interface Props {
  collectionId: number | null;
  onClose: () => void;
}

const CollectionDetailModal: React.FC<Props> = ({
  collectionId,
  onClose,
}: Props) => {
  const [preview, setPreview] = useState<string | null>(null);
  const {
    data: detail,
    isLoading,
    error,
    refetch,
  } = useCollection(collectionId ?? undefined);

  const notFound = collectionsResultStatus(error) === 404;

  const hasPoint = detail?.lat != null && detail?.lng != null;

  const attemptJourneys = useMemo(() => {
    const ids = new Set<string>();
    (detail?.logs ?? []).forEach((log) => {
      if (log.journeyId) ids.add(log.journeyId);
      if (log.metadata?.fromJourneyId)
        ids.add(String(log.metadata.fromJourneyId));
    });
    return Array.from(ids);
  }, [detail?.logs]);

  const {
    events: geofenceEvents,
    isFetching: isFetchingGeofences,
    failedJourneys,
  } = useCollectionGeofenceEvents(
    detail?.id,
    attemptJourneys,
    collectionId != null,
  );
  const [showGeofences, setShowGeofences] = useState(true);

  const eventJourneys = (log: CollectionLog) => {
    const ids = [log.metadata?.fromJourneyId, log.journeyId].filter(
      Boolean,
    ) as string[];
    return ids;
  };

  const timelineItems = useMemo(() => {
    const entries = [
      ...(detail?.logs ?? []).map((log) => ({
        at: log.occurredAt,
        log,
        geofence: undefined as (typeof geofenceEvents)[number] | undefined,
      })),
      ...geofenceEvents.map((geofence) => ({
        at: geofence.occurredAt ?? '',
        log: undefined as CollectionLog | undefined,
        geofence,
      })),
    ];
    entries.sort((a, b) => (a.at ?? '').localeCompare(b.at ?? ''));
    return entries;
  }, [detail?.logs, geofenceEvents]);

  const eventExtras = (log: CollectionLog) => {
    const parts: React.ReactNode[] = [];
    if (log.metadata?.packageCount != null) {
      parts.push(
        <Tag key="count" color="blue">
          {log.metadata.packageCount} bultos declarados
        </Tag>,
      );
    }
    if (log.metadata?.externalCode) {
      parts.push(
        <Tag key="code" color="green">
          {log.metadata.externalCode}
        </Tag>,
      );
    }
    if (log.event === 'received') {
      const { declaredPackageCount, scannedCount, receivedPackageCount } =
        log.metadata ?? {};
      if (receivedPackageCount != null) {
        parts.push(
          <Tag key="received" color="green">
            {receivedPackageCount} bultos recibidos
          </Tag>,
        );
      }
      if (scannedCount != null) {
        parts.push(<Tag key="scanned">{scannedCount} escaneados</Tag>);
      }
      if (declaredPackageCount != null) {
        parts.push(
          <Tag key="declared">{declaredPackageCount} declaró el driver</Tag>,
        );
      }
    }
    if (log.metadata?.leftEarly) {
      const remaining = log.metadata.remainingWaitSeconds;
      parts.push(
        <Tag key="early" color="orange">
          Se fue antes de tiempo
          {remaining ? ` (faltaban ${Math.ceil(remaining / 60)} min)` : ''}
        </Tag>,
      );
    }
    if (log.metadata?.releasedByOperatorId != null) {
      parts.push(
        <Tag key="released" color="orange">
          Espera liberada por{' '}
          {log.metadata.releasedByOperatorName ??
            `operador #${log.metadata.releasedByOperatorId}`}
        </Tag>,
      );
    }
    if (log.metadata?.waitedSeconds != null) {
      parts.push(
        <Tag key="wait">
          Esperó {Math.round(log.metadata.waitedSeconds / 60)} min
        </Tag>,
      );
    }
    if (log.metadata?.measuredOnDevice) {
      parts.push(
        <Tooltip
          key="device"
          title="El driver estaba sin conexión: la espera se midió con el reloj del teléfono."
        >
          <Tag color="purple">espera medida en el celular</Tag>
        </Tooltip>,
      );
    }
    return parts;
  };

  const logItem = (log: CollectionLog) => ({
    color: EVENT_COLORS[log.event] ?? 'blue',
    children: (
      <Space direction="vertical" size={4}>
        <Space wrap>
          <Text strong>{eventLabel(log)}</Text>
          <Text type="secondary">{fmt(log.occurredAt)}</Text>
          {log.event === 'arrived' && (
            <Text type="secondary">
              (inicio pedido {fmt(detail?.scheduledStart ?? '')})
            </Text>
          )}
          {eventJourneys(log).map((journeyId, index) => (
            <React.Fragment key={journeyId}>
              {index > 0 && <Text type="secondary">→</Text>}
              <Link to={`${SITELINKS.journey}/${journeyId}`}>{journeyId}</Link>
            </React.Fragment>
          ))}
        </Space>
        <Text type="secondary">{collectionActor(log)}</Text>
        <Space wrap>{eventExtras(log)}</Space>
        {log.reason && <Text italic>{log.reason}</Text>}
        {log.metadata?.photoUrls && log.metadata.photoUrls.length > 0 && (
          <Space size={[4, 8]} wrap>
            {log.metadata.photoUrls.map((url) => (
              <Image
                key={url}
                src={url}
                width={64}
                height={64}
                style={{ objectFit: 'cover', borderRadius: 4 }}
                preview={{
                  visible: preview === url,
                  onVisibleChange: (visible) =>
                    setPreview(visible ? url : null),
                }}
                onClick={() => setPreview(url)}
              />
            ))}
          </Space>
        )}
      </Space>
    ),
  });

  return (
    <Modal
      open={collectionId != null}
      onCancel={onClose}
      width={900}
      footer={null}
      destroyOnClose
      title={
        detail ? (
          <Space>
            <span>
              Colecta #{detail.id} · {detail.storeName}
            </span>
            <Tag color={COLLECTION_STATUS_COLORS[detail.status]}>
              {COLLECTION_STATUS_LABELS[detail.status]}
            </Tag>
            <Tooltip
              title={
                isFetchingGeofences
                  ? 'Buscando el recorrido registrado'
                  : 'Exportar remito'
              }
            >
              <Button
                size="small"
                icon={<FilePdfOutlined />}
                loading={isFetchingGeofences}
                disabled={isFetchingGeofences}
                onClick={() => exportCollectionRemitoV2(detail, geofenceEvents)}
              >
                Remito
              </Button>
            </Tooltip>
          </Space>
        ) : (
          'Colecta'
        )
      }
    >
      {isLoading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <Spin spinning />
        </div>
      )}

      {!isLoading && error && (
        <Alert
          type="error"
          showIcon
          message={
            notFound
              ? `No se encontró la colecta #${collectionId}`
              : 'No se pudo traer la colecta'
          }
          description={notFound ? undefined : (error as Error).message}
          action={
            notFound ? undefined : (
              <Button size="small" danger onClick={() => refetch()}>
                Reintentar
              </Button>
            )
          }
        />
      )}

      {detail && (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Descriptions column={3} size="small" bordered>
            <Descriptions.Item label="Marketplace">
              {detail.marketplaceName}
            </Descriptions.Item>
            <Descriptions.Item label="Dirección" span={2}>
              {detail.address}
            </Descriptions.Item>
            <Descriptions.Item label="Inicio pedido">
              {fmt(detail.scheduledStart)}
            </Descriptions.Item>
            <Descriptions.Item label="Duración">
              {detail.estimatedDurationMinutes} min
            </Descriptions.Item>
            <Descriptions.Item label="Ruta">
              {detail.journeyId ? (
                <Link to={`${SITELINKS.journey}/${detail.journeyId}`}>
                  {detail.journeyId}
                </Link>
              ) : (
                '-'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Retiró">
              {detail.driverName ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Patente">
              {detail.vehiclePlate ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Declarados">
              {detail.declaredPackageCount ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Escaneados">
              {detail.intakeCodes?.length ?? 0}
            </Descriptions.Item>
            <Descriptions.Item label="Recibidos">
              {detail.receivedPackageCount ?? '-'}
            </Descriptions.Item>
          </Descriptions>

          {detail.intakeCodes?.length > 0 && (
            <div>
              <Text strong>Códigos escaneados</Text>
              <div style={{ marginTop: 8 }}>
                <Space size={[4, 8]} wrap>
                  {detail.intakeCodes.map((code) => (
                    <Tooltip
                      key={code.externalCode}
                      title={fmt(code.ingestedAt)}
                    >
                      <Tag color="green">{code.externalCode}</Tag>
                    </Tooltip>
                  ))}
                </Space>
              </div>
            </div>
          )}

          <div>
            <Text strong>Línea de tiempo</Text>
            <div style={{ marginTop: 12 }}>
              <Timeline
                items={timelineItems.map((entry) =>
                  entry.geofence
                    ? {
                        color:
                          entry.geofence.type === 'enter' ? 'green' : 'gray',
                        children: (
                          <Space direction="vertical" size={4}>
                            <Space size={8}>
                              <Text strong>
                                {entry.geofence.type === 'enter'
                                  ? 'Entró al radio'
                                  : 'Salió del radio'}
                              </Text>
                              <Tag>
                                {entry.geofence.placeType === 'collection'
                                  ? 'Tienda'
                                  : 'Depósito'}
                              </Tag>
                            </Space>
                            <Text type="secondary">
                              {entry.geofence.occurredAt
                                ? dayjs(entry.geofence.occurredAt).format(
                                    'DD/MM/YYYY HH:mm',
                                  )
                                : '-'}
                              {' · '}
                              <Link
                                to={`${SITELINKS.journey}/${entry.geofence.journeyId}`}
                              >
                                {entry.geofence.journeyId}
                              </Link>
                              {' · hora del teléfono'}
                            </Text>
                          </Space>
                        ),
                      }
                    : logItem(entry.log as CollectionLog),
                )}
              />
            </div>
          </div>

          {(geofenceEvents.length > 0 || failedJourneys.length > 0) && (
            <div>
              <Typography.Text strong>Recorrido registrado</Typography.Text>
              <Typography.Paragraph
                type="secondary"
                style={{ marginBottom: 8 }}
              >
                Entradas y salidas que registró sola la aplicación. La hora la
                midió el teléfono, que es donde ocurrieron.
              </Typography.Paragraph>
              {failedJourneys.length > 0 && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 8 }}
                  message={`No se pudo leer el historial de ${
                    failedJourneys.length === 1 ? 'la ruta' : 'las rutas'
                  } ${failedJourneys.join(', ')}. El recorrido puede estar incompleto.`}
                />
              )}
              <Table
                size="small"
                rowKey={(event) => event.eventId}
                dataSource={geofenceEvents}
                pagination={false}
                columns={[
                  {
                    title: 'Evento',
                    width: 100,
                    render: (event) => (
                      <Tag color={event.type === 'enter' ? 'green' : 'default'}>
                        {event.type === 'enter' ? 'Entrada' : 'Salida'}
                      </Tag>
                    ),
                  },
                  {
                    title: 'Lugar',
                    width: 110,
                    render: (event) =>
                      event.placeType === 'collection' ? 'Tienda' : 'Depósito',
                  },
                  {
                    title: 'Hora',
                    width: 170,
                    render: (event) =>
                      event.occurredAt
                        ? dayjs(event.occurredAt).format('DD/MM/YYYY HH:mm')
                        : '-',
                  },
                  {
                    title: 'Ruta',
                    render: (event) => (
                      <Link to={`${SITELINKS.journey}/${event.journeyId}`}>
                        {event.journeyId}
                      </Link>
                    ),
                  },
                  {
                    title: 'Ubicación',
                    width: 150,
                    render: (event) =>
                      event.lat != null && event.lng != null ? (
                        <a
                          href={`https://www.google.com/maps?q=${event.lat},${event.lng}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {event.lat.toFixed(5)}, {event.lng.toFixed(5)}
                        </a>
                      ) : (
                        '-'
                      ),
                  },
                ]}
              />
            </div>
          )}

          {hasPoint && (
            <div style={{ height: 260 }}>
              {geofenceEvents.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <Switch
                    size="small"
                    checked={showGeofences}
                    onChange={setShowGeofences}
                  />{' '}
                  <Typography.Text type="secondary">
                    Mostrar el recorrido registrado
                  </Typography.Text>
                </div>
              )}
              <MapContainer
                center={[detail.lat, detail.lng]}
                zoom={16}
                style={{ height: '100%', width: '100%' }}
              >
                <MapInvalidator />
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <Marker
                  position={[detail.lat, detail.lng]}
                  icon={defaultMarkerIcon}
                />
                {showGeofences && (
                  <>
                    <Circle
                      center={[detail.lat, detail.lng]}
                      radius={GEOFENCE_RADIUS_METERS}
                      pathOptions={{ color: '#0db8b2', weight: 1 }}
                    />
                    {geofenceEvents
                      .filter((event) => event.lat != null && event.lng != null)
                      .map((event) => (
                        <Circle
                          key={event.eventId}
                          center={[event.lat as number, event.lng as number]}
                          radius={12}
                          pathOptions={{
                            color: event.type === 'enter' ? 'green' : 'gray',
                          }}
                        />
                      ))}
                  </>
                )}
              </MapContainer>
            </div>
          )}
        </Space>
      )}
    </Modal>
  );
};

export default CollectionDetailModal;
